import { PreSaleBanner } from '../components/layout/PreSaleBanner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import DOMPurify from 'dompurify';

const EN_CONTENT = `
<h2>Privacy Policy</h2>
<p><em>Last updated: 1 January 2026</em></p>

<h3>1. Who we are</h3>
<p>Sleepstack (KVK: 88291308, BTW: NL230290541B02) is responsible for processing your personal data as described in this privacy policy.</p>
<p>Contact: <a href="mailto:info@sleepstack.nl">info@sleepstack.nl</a></p>

<h3>2. What data we collect</h3>
<ul>
<li><strong>Account data:</strong> Email address, name (if provided)</li>
<li><strong>Order data:</strong> Name, email, shipping address, order details</li>
<li><strong>Payment data:</strong> Processed by Stripe. We do not store full card details.</li>
<li><strong>Usage data:</strong> IP address, browser type, pages visited (analytics, opt-in only)</li>
</ul>

<h3>3. Why we process your data</h3>
<ul>
<li>To fulfil your order and deliver your product</li>
<li>To send transactional emails (order confirmation, shipping updates)</li>
<li>To manage your account</li>
<li>To comply with legal obligations</li>
</ul>

<h3>4. Legal basis</h3>
<p>We process your data on the basis of contract performance (to fulfil your order), legal obligation, and, for analytics, your consent.</p>

<h3>5. Data sharing</h3>
<p>We share your data with: Stripe (payment processing), delivery services (for shipping), our email provider Resend (transactional emails), and, for analytics, only after you opt in, Google Analytics 4 (Google Ireland Ltd.). We do not sell your data.</p>

<h3>6. Your rights</h3>
<p>You have the right to access, correct, delete, or restrict processing of your data. To exercise these rights, email <a href="mailto:info@sleepstack.nl">info@sleepstack.nl</a>.</p>

<h3>7. Data retention</h3>
<p>Order data is retained for 7 years for tax purposes. Account data is retained until account deletion. Analytics data is anonymised.</p>

<h3>8. Security</h3>
<p>We use SSL/TLS encryption, secure hosting, and access controls to protect your data.</p>

<h3>9. Cookies</h3>
<p>We use strictly necessary cookies for site functionality. Analytics cookies (Google Analytics 4) and marketing cookies only fire after you grant consent via the cookie banner. We use Google Consent Mode v2: by default all analytics and advertising storage is denied; we only switch to "granted" once you opt in.</p>

<h3>10. Contact</h3>
<p>Questions about this policy: <a href="mailto:info@sleepstack.nl">info@sleepstack.nl</a></p>
`;

const NL_CONTENT = `
<h2>Privacybeleid</h2>
<p><em>Laatste update: 1 januari 2026</em></p>

<h3>1. Wie zijn wij</h3>
<p>Sleepstack (KVK: 88291308, BTW: NL230290541B02) is verantwoordelijk voor de verwerking van uw persoonsgegevens zoals beschreven in dit privacybeleid.</p>
<p>Contact: <a href="mailto:info@sleepstack.nl">info@sleepstack.nl</a></p>

<h3>2. Welke gegevens verzamelen wij</h3>
<ul>
<li><strong>Accountgegevens:</strong> e-mailadres, naam (indien opgegeven)</li>
<li><strong>Bestelgegevens:</strong> naam, e-mail, bezorgadres, bestelgegevens</li>
<li><strong>Betalingsgegevens:</strong> verwerkt door Stripe. Wij slaan geen volledige kaartgegevens op.</li>
<li><strong>Gebruiksgegevens:</strong> IP-adres, browsertype, bezochte pagina's (analytics, alleen na opt-in)</li>
</ul>

<h3>3. Waarom wij uw gegevens verwerken</h3>
<ul>
<li>Om uw bestelling te verwerken en uw product te leveren</li>
<li>Om transactionele e-mails te versturen (orderbevestiging, verzendupdates)</li>
<li>Om uw account te beheren</li>
<li>Om te voldoen aan wettelijke verplichtingen</li>
</ul>

<h3>4. Rechtsgrondslag</h3>
<p>Wij verwerken uw gegevens op basis van de uitvoering van een overeenkomst (om uw bestelling uit te voeren), een wettelijke verplichting en, voor analytics, uw toestemming.</p>

<h3>5. Delen van gegevens</h3>
<p>Wij delen uw gegevens met: Stripe (betalingsverwerking), bezorgdiensten (voor verzending), onze e-mailprovider Resend (transactionele e-mails) en, voor analytics, alleen na uw opt-in, Google Analytics 4 (Google Ireland Ltd.). Wij verkopen uw gegevens niet.</p>

<h3>6. Uw rechten</h3>
<p>U heeft het recht op inzage, correctie, verwijdering of beperking van de verwerking van uw gegevens, alsmede het recht op dataportabiliteit en het recht om bezwaar te maken. Stuur een e-mail naar <a href="mailto:info@sleepstack.nl">info@sleepstack.nl</a> om deze rechten uit te oefenen.</p>

<h3>7. Bewaartermijnen</h3>
<p>Bestelgegevens worden 7 jaar bewaard voor fiscale doeleinden. Accountgegevens worden bewaard tot u uw account verwijdert. Analytics-gegevens worden geanonimiseerd.</p>

<h3>8. Beveiliging</h3>
<p>Wij gebruiken SSL/TLS-versleuteling, beveiligde hosting en toegangscontroles om uw gegevens te beschermen.</p>

<h3>9. Cookies</h3>
<p>Wij gebruiken strikt noodzakelijke cookies voor het functioneren van de site. Analytische cookies (Google Analytics 4) en marketingcookies worden pas geplaatst nadat u via de cookiebanner toestemming geeft. We gebruiken Google Consent Mode v2: standaard zijn alle analytics- en advertentie-opslag geweigerd; we schakelen pas naar "granted" zodra u opt-in geeft.</p>

<h3>10. Contact</h3>
<p>Vragen over dit beleid: <a href="mailto:info@sleepstack.nl">info@sleepstack.nl</a></p>
<p>U heeft daarnaast het recht om een klacht in te dienen bij de Autoriteit Persoonsgegevens (<a href="https://autoriteitpersoonsgegevens.nl">autoriteitpersoonsgegevens.nl</a>).</p>
`;

export default function PrivacyPolicy() {
    const { lang } = useLanguage();
    const content = lang === 'nl' ? NL_CONTENT : EN_CONTENT;

    return (
        <div>
            <SEO
                title="Privacy Policy | Sleepstack"
                description="How Sleepstack collects, uses, and protects your personal data. GDPR-compliant, EU-based, minimal data collection by design."
                path="/privacy"
            />
            <PreSaleBanner />
            <Navbar />
            <main style={{ paddingTop: 'var(--page-offset, 128px)' }}>
                <section className="section-padding bg-white">
                    <div className="section-container max-w-2xl">
                        <h1 className="heading-xl text-foreground mb-8" data-testid="privacy-title">
                            {lang === 'nl' ? 'Privacybeleid' : 'Privacy Policy'}
                        </h1>
                        <div
                            className="prose prose-slate max-w-none article-content"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
                            data-testid="privacy-content"
                        />
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
